/**
 * B30: a phone alert tapped while the Hub is already open.
 *
 * The worker (public/firebase-messaging-sw.js, openHubLink) brings the open
 * Hub window forward and asks it, through this listener, to show the alert's
 * page. The page answers on the MessageChannel port the worker sent, so the
 * worker knows whether it was handled:
 *
 *   - handled: the in-app router moved to the link. No reload, so nothing on
 *     screen is thrown away and the app does not boot again.
 *   - unsaved: something on screen would be lost (alertTapWouldLoseWork, the
 *     same check that holds off the automatic reloads). The page does
 *     NOT move. This router has no navigation guard of its own (a plain
 *     <Routes> tree, no useBlocker), and an in-app move would unmount a half
 *     written message just as surely as a reload. The worker opens the link
 *     in a new window instead, and the work stays where it was.
 *   - invalid: the link is not a Hub page on this site. Nothing moves.
 *
 * A page that does not answer is never reloaded either; the worker opens a
 * new window instead.
 *
 * Why not a plain WindowClient.navigate: that is a full reload, and iPhone
 * Safari ignores the browser leave warning, so it protects nothing there.
 *
 * Framework light: the decision is a plain function so it is tested without a
 * browser (tests/alert-tap-routing.test.mjs).
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { hasRegisteredUnsavedWork, uploadInFlight } from './unsavedWork';
import { domHasUnsavedText } from './unsavedWorkDom';

export const ALERT_TAP_MESSAGE_TYPE = 'ldg-alert-tap';

export type AlertTapReply = { handled: true } | { handled: false; reason: 'unsaved' | 'invalid' };

export type AlertTapDeps = {
  origin: string;
  navigate: (to: string) => void;
  hasUnsavedWork: () => boolean;
};

/** The in-app path for a link, or null when it is not a page on this site. */
export function alertTapPath(link: unknown, origin: string): string | null {
  if (typeof link !== 'string' || !link) return null;
  try {
    const url = new URL(link, origin);
    if (url.origin !== origin) return null;
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

/** Decide and act on one message from the worker. Null when it is not ours. */
export function handleAlertTapMessage(data: unknown, deps: AlertTapDeps): AlertTapReply | null {
  if (!data || typeof data !== 'object') return null;
  const message = data as { type?: unknown; link?: unknown };
  if (message.type !== ALERT_TAP_MESSAGE_TYPE) return null;
  const path = alertTapPath(message.link, deps.origin);
  if (!path) return { handled: false, reason: 'invalid' };
  if (deps.hasUnsavedWork()) return { handled: false, reason: 'unsaved' };
  deps.navigate(path);
  return { handled: true };
}

type MessageSource = {
  addEventListener: (type: 'message', listener: (event: MessageEvent) => void) => void;
  removeEventListener: (type: 'message', listener: (event: MessageEvent) => void) => void;
};

/** Install the listener on a service worker container. Returns the remover. */
export function installAlertTapListener(container: MessageSource, deps: AlertTapDeps): () => void {
  const onMessage = (event: MessageEvent) => {
    const reply = handleAlertTapMessage(event.data, deps);
    if (!reply) return;
    const port = event.ports && event.ports[0];
    try {
      port?.postMessage(reply);
    } catch {
      // The worker stopped waiting; it falls back on its own.
    }
  };
  container.addEventListener('message', onMessage);
  return () => container.removeEventListener('message', onMessage);
}

/**
 * Would moving the page right now throw something away? The same three
 * questions every other page-moving guard asks (lib/lazyRoute.ts
 * reloadWouldLoseWork): a registered leave warning, an upload still sending,
 * or words typed in a box on a page that never registered (AdminBoard,
 * CompParentsBoard, the Assistant). An in-app move unmounts the page just as
 * a reload does.
 */
export function alertTapWouldLoseWork(): boolean {
  return hasRegisteredUnsavedWork() || uploadInFlight() || domHasUnsavedText();
}

/** Mounted once, inside the router (App.tsx). */
export function useAlertTapRouting(): void {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    const container = navigator.serviceWorker;
    if (!container) return;
    return installAlertTapListener(container, {
      origin: window.location.origin,
      navigate: (to) => navigateRef.current(to),
      hasUnsavedWork: alertTapWouldLoseWork,
    });
  }, []);
}
